import { CellStatus } from 'components/Table/helpers';
import { dateToFormat, serverDateToFormat } from 'helpers/formatters/date';
import { useEffect, useState } from 'react';
import { Benchmark } from 'types/Benchmark';
import { Api } from '../../../../api';
import { differenceInDays, isAfter, isBefore } from 'date-fns';
import {
  DefaultTableSort,
  FilterPlaceholders,
  FilterTypes,
  SortbyDateOptions,
  SortByDateTypes,
  SortByTextOptions,
  SortByTextTypes,
  SortType,
} from 'components/Table/helpers/tableColumnSort';
import { CellBenchmarkName } from '../../../../components/Table/helpers/CellBenchmarkName/CellBenchmarkName';
import { CellLagDate } from 'components/Table/helpers/CellLagDate/CellLagDate';

export const useTable = ({
  selectColumnFilter,
  dateFilter,
  defaultLagDate,
  pageIndex,
  pageSize,
}: any) => {
  const [data, setData] = useState<Benchmark[]>([]);
  const [initData, setInitData] = useState<Benchmark[]>([]);
  const { data: benchmarkData, isLoading: isLoadingBenchmarks } =
    Api.benchmarks.GetBenchmarks();
  const storedSort = localStorage.getItem('sort');
  const [sort, setSort] = useState<any>(
    storedSort && storedSort !== '{}'
      ? JSON.parse(storedSort)
      : DefaultTableSort
  );
  const [filter, setFilter] = useState<any>({});

  const sortByDate = (key: string, value: any) => {
    setSort({ [key]: { ...value, type: SortType.Date } });
  };

  const sortByText = (key: string, value: any) => {
    setSort({ [key]: { ...value, type: SortType.Text } });
  };

  const sortLagDate = (data: Array<any>, key: string, value: any) => {
    const today = new Date();

    let { notLaggedBenchmarks, laggedBenchmarks, laggedEmptyBenchmarks } =
      data.reduce(
        (result, benchmark) => {
          if (benchmark.isLagged) {
            benchmark.lagDate !== FilterTypes.Dash
              ? result.laggedBenchmarks.push(benchmark)
              : result.laggedEmptyBenchmarks.push(benchmark);
          } else {
            result.notLaggedBenchmarks.push(benchmark);
          }

          return result;
        },
        {
          notLaggedBenchmarks: [],
          laggedBenchmarks: [],
          laggedEmptyBenchmarks: [],
        }
      );

    laggedBenchmarks = laggedBenchmarks.map((benchmark: any) => {
      let lagDate: Date =
        benchmark.lagDate !== FilterTypes.Dash
          ? new Date(benchmark.lagDate)
          : value.label === SortByDateTypes.MostRecent
          ? new Date(8640000000000000)
          : new Date(0);

      return {
        ...benchmark,
        differenceFromToday: differenceInDays(lagDate, today) * -1,
      };
    });

    laggedBenchmarks = laggedBenchmarks.sort(
      (benchmark1: any, benchmark2: any) => {
        const [value1, value2] = [
          benchmark1.differenceFromToday,
          benchmark2.differenceFromToday,
        ];

        if (value1 < value2) {
          return value.label === SortByDateTypes.MostRecent ? -1 : 1;
        } else if (value1 === value2) {
          return 0;
        } else {
          return value.label === SortByDateTypes.MostRecent ? 1 : -1;
        }
      }
    );

    return [
      ...laggedBenchmarks,
      ...laggedEmptyBenchmarks,
      ...notLaggedBenchmarks,
    ];
  };

  const doSortByDate = (data: Array<any>, key: string, value: any) => {
    if (key === 'lagDate') {
      return sortLagDate(data, key, value);
    }

    const sortedData = [...data].sort((a: any, b: any) => {
      let valueA =
        a[key] === FilterTypes.Dash
          ? value.label === SortByDateTypes.MostRecent
            ? new Date(0)
            : new Date(8640000000000000)
          : new Date(a[key]);

      let valueB =
        b[key] === FilterTypes.Dash
          ? value.label === SortByDateTypes.MostRecent
            ? new Date(0)
            : new Date(8640000000000000)
          : new Date(b[key]);

      if (isAfter(valueA, valueB)) {
        return value.label === SortByDateTypes.MostRecent ? -1 : 1;
      }

      if (isBefore(valueA, valueB)) {
        return value.label === SortByDateTypes.MostRecent ? 1 : -1;
      }

      return 0;
    });

    return sortedData;
  };

  const doSortByText = (data: Array<any>, key: string, value: any) => {
    const sortedData = [...data].sort((a: any, b: any) => {
      const nameA = a[key].toLowerCase().trim();
      const nameB = b[key].toLowerCase().trim();

      if (nameA < nameB) {
        return value.label === SortByTextTypes.AZ ? -1 : 1;
      }

      if (nameA > nameB) {
        return value.label === SortByTextTypes.AZ ? 1 : -1;
      }

      return 0;
    });

    return sortedData;
  };

  const doFilterData = (data: Array<any>, filters: any) => {
    var filterKeys = Object.keys(filters);
    return data.filter(function (item) {
      return filterKeys.every(function (key) {
        const val =
          filters[key].label === FilterTypes.None
            ? FilterTypes.Dash
            : filters[key].label;
        if (!val) {
          return true;
        }
        return item[key].includes(val);
      });
    });
  };

  const handleFilter = (key: string, value: any) => {
    if (value.label === FilterPlaceholders.All) {
      delete filter[key];
      setFilter({ ...filter });
    } else {
      setFilter({ ...filter, [key]: value });
    }
  };

  const resetTable = () => {
    setFilter({});
    setSort(DefaultTableSort);
  };

  useEffect(() => {
    const filteredData = doFilterData(initData, filter);

    if (Object.keys(sort).length > 0) {
      const key = Object.keys(sort)[0];
      const sortedData =
        sort[key].type === SortType.Date
          ? doSortByDate(filteredData, key, sort[key])
          : doSortByText(filteredData, key, sort[key]);
      setData(sortedData);
    } else {
      setData(filteredData);
    }
  }, [initData, filter, sort]);

  useEffect(() => {
    localStorage.setItem('sort', JSON.stringify(sort));
  }, [sort]);

  useEffect(() => {
    if (benchmarkData) {
      const transformedData = benchmarkData?.map((item: any) => {
        return {
          ...item,
          calculationFrequency: item.calculationFrequency?.name || '-',
          rebalanceFrequency: item.rebalanceFrequency?.name || '-',
          type: item.type?.name || '-',
          inceptionDate: serverDateToFormat(item.inceptionDate, 'MM/dd/y'),
          dateCreated: dateToFormat(new Date(item.dateCreated), 'MM/dd/y'),
          lastRun: item.lastRun?.date
            ? dateToFormat(new Date(item.lastRun.date), 'MM/dd/y h:mm a')
            : '-',
          status: item.lastRun?.status?.name || '-',
          typeId: item.type?.id || '-',
          lagDate:
            item.customLagDate !== undefined
              ? serverDateToFormat(item.customLagDate, 'MM/dd/y')
              : defaultLagDate !== undefined
              ? serverDateToFormat(defaultLagDate, 'MM/dd/y')
              : '-',
        };
      });
      setData(transformedData);
      setInitData(transformedData);
    }
  }, [benchmarkData, defaultLagDate]);

  const columns = [
    {
      Header: 'Name',
      accessor: 'name',
      testid: 'name',
      Cell: (props: any) =>
        CellBenchmarkName(props, Number(pageIndex + 1), Number(pageSize), 250),
      SortedBy: () =>
        dateFilter({
          placeholder: FilterPlaceholders.SortBy,
          options: SortByTextOptions,
          value: sort['name'],
          onChange: (props: any) => sortByText('name', props),
        }),
    },
    {
      Header: 'Type',
      accessor: 'type',
      testid: 'type',
      hasFilter: true,
      Filter: (props: any) =>
        selectColumnFilter({
          ...props,
          placeholder: FilterPlaceholders.All,
          value: filter['type'],
          initData,
          onChange: (props: any) => handleFilter('type', props),
        }),
      disableGlobalFilter: true,
    },
    {
      Header: 'Calculation Frequency',
      accessor: 'calculationFrequency',
      testid: 'calculationFrequency',
      hasFilter: true,
      Filter: (props: any) =>
        selectColumnFilter({
          ...props,
          placeholder: FilterPlaceholders.All,
          value: filter['calculationFrequency'],
          initData,
          onChange: (props: any) => handleFilter('calculationFrequency', props),
        }),
      disableGlobalFilter: true,
    },
    {
      Header: 'Rebalance Frequency',
      accessor: 'rebalanceFrequency',
      testid: 'rebalanceFrequency',
      hasFilter: true,
      Filter: (props: any) =>
        selectColumnFilter({
          ...props,
          placeholder: FilterPlaceholders.All,
          value: filter['rebalanceFrequency'],
          initData,
          onChange: (props: any) => handleFilter('rebalanceFrequency', props),
        }),
      disableGlobalFilter: true,
    },
    {
      Header: 'Base Currency',
      accessor: 'baseCurrencyCode',
      testid: 'baseCurrency',
      hasFilter: true,
      Filter: (props: any) =>
        selectColumnFilter({
          ...props,
          placeholder: FilterPlaceholders.All,
          value: filter['baseCurrencyCode'],
          initData,
          onChange: (props: any) => handleFilter('baseCurrencyCode', props),
        }),
      disableGlobalFilter: true,
    },
    {
      Header: 'Inception Date',
      accessor: 'inceptionDate',
      testid: 'inceptionDate',
      SortedBy: () =>
        dateFilter({
          placeholder: FilterPlaceholders.SortBy,
          value: sort['inceptionDate'],
          options: SortbyDateOptions,
          onChange: (props: any) => sortByDate('inceptionDate', props),
        }),
      disableGlobalFilter: true,
    },
    {
      Header: 'Lag Date',
      accessor: 'lagDate',
      testid: 'lagDate',
      Cell: (props: any) => CellLagDate(props, defaultLagDate),
      SortedBy: () =>
        dateFilter({
          placeholder: FilterPlaceholders.SortBy,
          value: sort['lagDate'],
          options: SortbyDateOptions,
          onChange: (props: any) => sortByDate('lagDate', props),
        }),
      disableGlobalFilter: true,
    },
    {
      Header: 'Run Date/Time',
      accessor: 'lastRun',
      testid: 'lastRun',
      SortedBy: () =>
        dateFilter({
          placeholder: FilterPlaceholders.SortBy,
          value: sort['lastRun'],
          options: SortbyDateOptions,
          onChange: (props: any) => sortByDate('lastRun', props),
        }),
      disableGlobalFilter: true,
    },
    {
      Header: 'Status',
      accessor: 'status',
      testid: 'status',
      hasFilter: true,
      Filter: (props: any) =>
        selectColumnFilter({
          ...props,
          placeholder: FilterPlaceholders.All,
          value: filter['status'],
          initData,
          onChange: (props: any) => handleFilter('status', props),
        }),
      Cell: CellStatus('status'),
      disableGlobalFilter: true,
    },
  ];

  const table = {
    data,
    columns,
  };

  return {
    table,
    isLoadingBenchmarks,
    resetTable,
  };
};
